import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './ReportHamburgerMenu.css';
import { COUNSELLOR_OVERLAY_CONFIG } from '../overlays/counsellorOverlayConfig';

const LANGUAGE_OPTIONS = [
  { code: 'en', labelKey: 'report_language_en', badge: 'EN' },
  { code: 'vi', labelKey: 'report_language_vi', badge: 'VI' },
  { code: 'fr', labelKey: 'report_language_fr', badge: 'FR' },
  { code: 'es', labelKey: 'report_language_es', badge: 'ES' },
  { code: 'zh', labelKey: 'report_language_zh', badge: 'ZH' },
  { code: 'ar', labelKey: 'report_language_ar', badge: 'AR' },
];

const MENU_VIEWPORT_MARGIN = 8;
const MENU_GAP_ABOVE = 10;
const MENU_GAP_BELOW = 14;

const CompactMenuIcon = ({ type }) => {
  const common = {
    className: 'menu-icon',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '1.7',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
  };

  switch (type) {
    case 'light':
      return <svg {...common}><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>;
    case 'dark':
      return <svg {...common}><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>;
    case 'account':
      return <svg {...common}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>;
    case 'more':
      return <svg {...common}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06A2 2 0 1 1 7.04 4.3l.06.06A1.65 1.65 0 0 0 8.92 4a1.65 1.65 0 0 0 1-1.51V2a2 2 0 0 1 4 0v.49a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.4.6.95 1 1.51 1H21a2 2 0 0 1 0 4h-.09c-.56 0-1.11.4-1.51 1Z" /></svg>;
    case 'signout':
      return <svg {...common}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>;
    case 'signin':
      return <svg {...common}><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" /></svg>;
    case 'about':
      return <svg {...common}><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>;
    case 'wai':
      return <svg {...common}><path d="M4 12c1.8-4.5 5-6.8 8-6.8s6.2 2.3 8 6.8c-1.8 4.5-5 6.8-8 6.8S5.8 16.5 4 12Z" /><path d="M9.8 12a2.2 2.2 0 1 0 4.4 0 2.2 2.2 0 0 0-4.4 0Z" /></svg>;
    case 'video':
      return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m10 9 5 3-5 3z" /></svg>;
    case 'clients':
      return <svg {...common}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /></svg>;
    case 'counsellors':
      return <svg {...common}><path d="M19.5 21v-2a7.5 7.5 0 0 0-15 0v2" /><circle cx="12" cy="7" r="4" /></svg>;
    case 'tutorial':
      return <svg {...common}><path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" /><path d="M9 7h6" /><path d="M9 11h8" /></svg>;
    case 'codes':
      return <svg {...common}><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15.5A2.5 2.5 0 0 0 17.5 16H4z" /><path d="M6.5 3v13" /><path d="M8.5 7h8" /><path d="M8.5 10.5h8" /></svg>;
    case 'skills':
      return <svg {...common}><ellipse cx="10" cy="8.5" rx="4.3" ry="5.8" /><path d="M7.4 5.3 12.6 11.7" /><path d="M12.9 13.1 19.5 19.7" /><circle cx="4.4" cy="18.6" r="1.8" fill="currentColor" stroke="none" /></svg>;
    case 'annotations':
      return <svg {...common}><path d="M4 5h16v11H9l-5 4V5Z" /><path d="M8 9h8" /><path d="M8 12h5" /></svg>;
    case 'suggestions':
      return <svg {...common}><path d="M9 18h6" /><path d="M10 22h4" /><path d="M12 2a6 6 0 0 0-3.8 10.6c.8.67 1.3 1.26 1.55 1.9h4.5c.25-.64.75-1.23 1.55-1.9A6 6 0 0 0 12 2Z" /><path d="M12 6v3" /><path d="M12 12h.01" /></svg>;
    case 'language':
      return <svg {...common}><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>;
    case 'guided':
      return <svg {...common}><path d="M12 3v3" /><path d="M12 18v3" /><path d="m5.64 5.64 2.12 2.12" /><path d="m16.24 16.24 2.12 2.12" /><path d="M3 12h3" /><path d="M18 12h3" /><path d="m5.64 18.36 2.12-2.12" /><path d="m16.24 7.76 2.12-2.12" /><circle cx="12" cy="12" r="3.5" /></svg>;
    case 'guided-on':
      return <svg {...common}><path d="M9 18h6" /><path d="M10 22h4" /><path d="M12 2a6 6 0 0 0-3.8 10.6c.8.67 1.3 1.26 1.55 1.9h4.5c.25-.64.75-1.23 1.55-1.9A6 6 0 0 0 12 2Z" /><path d="M12 6v3" /><path d="M12 12h.01" /></svg>;
    case 'guided-off':
      return <svg {...common} style={{ opacity: 0.45 }}><path d="M9 18h6" /><path d="M10 22h4" /><path d="M12 2a6 6 0 0 0-3.8 10.6c.8.67 1.3 1.26 1.55 1.9h4.5c.25-.64.75-1.23 1.55-1.9A6 6 0 0 0 12 2Z" /><path d="M3 3l18 18" /></svg>;
    case 'reflection':
      return <svg {...common}><path d="M5 8a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v4a3 3 0 0 1-3 3H10l-4 4v-4H8a3 3 0 0 1-3-3z" /><path d="M9 10h6" /></svg>;
    case 'affirm':
      return <svg {...common}><path d="M12 4.5 14.2 9l4.8.7-3.5 3.4.8 4.8L12 15.6 7.7 17.9l.8-4.8L5 9.7 9.8 9z" /><path d="M7 20h10" /></svg>;
    default:
      return <svg {...common}><circle cx="12" cy="12" r="9" /></svg>;
  }
};

function ReportHamburgerMenu({
  t,
  menuOpen,
  toggleMenu,
  closeMenu,
  menuRef,
  menuPortalRef,
  handleThemeChange,
  theme,
  onShowContacts,
  onShowAccount,
  onShowAbout,
  onShowWorkingAlliance,
  onShowLectures,
  onShowTutorial,
  onShowAutomatedMiscMiti,
  onShowSimpleReflections,
  onShowAffirmations,
  onShowComplexReflections,
  onShowClient,
  onShowCounsellors,
  guidedHighlightsEnabled = true,
  onGuidedHighlightsChange,
  skillsAnnotationsEnabled = true,
  onSkillsAnnotationsChange,
  suggestionsEnabled = true,
  onSuggestionsChange,
  language = 'en',
  onLanguageChange,
  authenticated = false,
  onSignOut,
  onSignIn,
  triggerVariant = 'hamburger',
  triggerContent = null,
  triggerClassName = '',
  triggerAriaLabel = 'Open menu',
  menuPlacement = 'below',
  submenuDirection = 'left',
  hideAboutMenu = false,
  hideContactsMenuItem = false,
  compactAccountMenu = false,
}) {
  const triggerRef = useRef(null);
  const floatingMenuRef = useRef(null);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langBtnRef = useRef(null);
  const [aboutMenuOpen, setAboutMenuOpen] = useState(false);
  const aboutBtnRef = useRef(null);
  const [skillsMenuOpen, setSkillsMenuOpen] = useState(false);
  const skillsBtnRef = useRef(null);
  const [counsellorsMenuOpen, setCounsellorsMenuOpen] = useState(false);
  const counsellorsBtnRef = useRef(null);
  const [guidedHighlightsMenuOpen, setGuidedHighlightsMenuOpen] = useState(false);
  const guidedHighlightsBtnRef = useRef(null);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [skillsAnnotationsMenuOpen, setSkillsAnnotationsMenuOpen] = useState(false);
  const [suggestionsMenuOpen, setSuggestionsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0, placement: menuPlacement });
  const [compactPanelOpen, setCompactPanelOpen] = useState(false);
  const [compactPanelView, setCompactPanelView] = useState('main');

  useEffect(() => {
    if (!menuOpen) {
      setLangMenuOpen(false);
      setAboutMenuOpen(false);
      setSkillsMenuOpen(false);
      setCounsellorsMenuOpen(false);
      setGuidedHighlightsMenuOpen(false);
      setSettingsMenuOpen(false);
      setSkillsAnnotationsMenuOpen(false);
      setSuggestionsMenuOpen(false);
    }
  }, [menuOpen]);

  useEffect(() => {
    if (!compactPanelOpen) {
      setCompactPanelView('main');
    }
  }, [compactPanelOpen]);

  const closeCompactPanel = () => {
    setCompactPanelOpen(false);
    setCompactPanelView('main');
  };

  const runCompactAction = (action) => {
    action && action();
    closeCompactPanel();
    closeMenu && closeMenu();
  };

  useLayoutEffect(() => {
    if (!menuOpen) return undefined;

    const updateMenuPosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const menuHeight = floatingMenuRef.current?.getBoundingClientRect().height || 0;
      const availableBelow = window.innerHeight - rect.bottom - MENU_GAP_BELOW - MENU_VIEWPORT_MARGIN;
      const resolvedPlacement = menuPlacement === 'below' && menuHeight > availableBelow
        ? 'above'
        : menuPlacement;
      const nextPosition = {
        top: resolvedPlacement === 'above' ? rect.top - MENU_GAP_ABOVE : rect.bottom + MENU_GAP_BELOW,
        // Anchor from the right edge so the fixed shell shrink-wraps into the
        // space to the LEFT of the trigger; anchoring with `left` would leave
        // it almost no room (the trigger sits near the right viewport edge).
        right: document.documentElement.clientWidth - rect.right,
        placement: resolvedPlacement,
      };
      setMenuPosition((currentPosition) => (
        currentPosition.top === nextPosition.top &&
        currentPosition.right === nextPosition.right &&
        currentPosition.placement === nextPosition.placement
          ? currentPosition
          : nextPosition
      ));
    };

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [menuOpen, menuPlacement]);

  useLayoutEffect(() => {
    if (!menuOpen) return undefined;

    const updateSubmenuDirections = () => {
      const submenus = floatingMenuRef.current?.querySelectorAll('.language-submenu');
      submenus?.forEach((submenu) => {
        submenu.classList.remove('language-submenu--open-upward');
        if (submenu.getBoundingClientRect().bottom > window.innerHeight - MENU_VIEWPORT_MARGIN) {
          submenu.classList.add('language-submenu--open-upward');
        }
      });
    };

    updateSubmenuDirections();
    const animationFrame = window.requestAnimationFrame(updateSubmenuDirections);
    window.addEventListener('resize', updateSubmenuDirections);
    window.addEventListener('scroll', updateSubmenuDirections, true);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', updateSubmenuDirections);
      window.removeEventListener('scroll', updateSubmenuDirections, true);
    };
  }, [
    aboutMenuOpen,
    counsellorsMenuOpen,
    guidedHighlightsMenuOpen,
    langMenuOpen,
    menuOpen,
    menuPosition,
    settingsMenuOpen,
    skillsAnnotationsMenuOpen,
    skillsMenuOpen,
    suggestionsMenuOpen,
  ]);

  const compactPanelTitle =
    compactPanelView === 'language'
      ? t('report_menu_language')
      : compactPanelView === 'guided'
        ? 'Guided Highlights'
      : compactPanelView === 'settings'
          ? 'Settings'
        : compactPanelView === 'skills-annotations'
          ? 'Skills Annotations'
        : compactPanelView === 'suggestions'
          ? 'Suggestions'
        : compactPanelView === 'skills'
          ? t('report_menu_specific_skills')
          : compactPanelView === 'counsellors'
            ? t('report_menu_counsellors')
            : 'App Menu';

  const compactPanelContent = compactPanelOpen ? (
    <div
      ref={menuPortalRef}
      className={`compact-options-overlay${theme === 'dark' ? ' dark' : ''}`}
      role="presentation"
      onClick={closeCompactPanel}
    >
      <div
        className="compact-options-panel"
        role="dialog"
        aria-modal="false"
        aria-label={compactPanelTitle}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="compact-options-header">
          {compactPanelView !== 'main' ? (
            <button
              type="button"
              className="compact-options-back"
              onClick={() => setCompactPanelView(
                compactPanelView === 'guided' ||
                compactPanelView === 'skills-annotations' ||
                compactPanelView === 'suggestions'
                  ? 'settings'
                  : 'main'
              )}
              aria-label="Back to more options"
            >
              ‹
            </button>
          ) : (
            <span className="compact-options-back-spacer" aria-hidden="true" />
          )}
          <h3>{compactPanelTitle}</h3>
          <button
            type="button"
            className="compact-options-close"
            onClick={closeCompactPanel}
            aria-label="Close more options"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="compact-options-body">
          {compactPanelView === 'main' && (
            <>
              <button className="menu-item" onClick={() => runCompactAction(onShowAbout)}>
                <CompactMenuIcon type="about" />
                {t('report_menu_about_aimie')}
              </button>
              <button className="menu-item" onClick={() => runCompactAction(onShowWorkingAlliance)}>
                <CompactMenuIcon type="wai" />
                {t('working_alliance_label')}
              </button>
              <button className="menu-item" onClick={() => runCompactAction(onShowLectures)}>
                <CompactMenuIcon type="video" />
                Lectures
              </button>
              <button className="menu-item" onClick={() => runCompactAction(onShowClient)}>
                <CompactMenuIcon type="clients" />
                {t('report_menu_clients')}
              </button>
              <button className="menu-item" onClick={() => setCompactPanelView('counsellors')}>
                <CompactMenuIcon type="counsellors" />
                {t('report_menu_counsellors')}
              </button>
              <button className="menu-item" onClick={() => runCompactAction(onShowTutorial)}>
                <CompactMenuIcon type="tutorial" />
                {t('report_menu_tutorial')}
              </button>
              <button className="menu-item" onClick={() => runCompactAction(onShowAutomatedMiscMiti)}>
                <CompactMenuIcon type="codes" />
                {t('report_menu_automated_misc_miti')}
              </button>
              <button className="menu-item" onClick={() => setCompactPanelView('skills')}>
                <CompactMenuIcon type="skills" />
                {t('report_menu_specific_skills')}
              </button>
              <button className="menu-item" onClick={() => setCompactPanelView('language')}>
                <CompactMenuIcon type="language" />
                {t('report_menu_language')}
              </button>
              <button className="menu-item" onClick={() => setCompactPanelView('settings')}>
                <CompactMenuIcon type="more" />
                Settings
              </button>
              {!hideContactsMenuItem && (
                <button className="menu-item" onClick={() => runCompactAction(onShowContacts)}>
                  <CompactMenuIcon type="account" />
                  {t('report_menu_contacts')}
                </button>
              )}
            </>
          )}

          {compactPanelView === 'language' && LANGUAGE_OPTIONS.map((option) => (
            <button
              key={option.code}
              className={`menu-item language-menu-item${language === option.code ? ' active' : ''}`}
              onClick={() => runCompactAction(() => onLanguageChange && onLanguageChange(option.code))}
            >
              <span className="language-badge">{option.badge}</span>
              <span className="language-name">{t(option.labelKey)}</span>
            </button>
          ))}

          {compactPanelView === 'guided' && (
            <>
              <button
                className={`menu-item${guidedHighlightsEnabled ? ' active' : ''}`}
                onClick={() => runCompactAction(() => onGuidedHighlightsChange && onGuidedHighlightsChange(true))}
              >
                <CompactMenuIcon type="guided-on" />
                Turn on
              </button>
              <button
                className={`menu-item${!guidedHighlightsEnabled ? ' active' : ''}`}
                onClick={() => runCompactAction(() => onGuidedHighlightsChange && onGuidedHighlightsChange(false))}
              >
                <CompactMenuIcon type="guided-off" />
                Turn off
              </button>
            </>
          )}

          {compactPanelView === 'settings' && (
            <>
              <button className="menu-item" onClick={() => setCompactPanelView('guided')}>
                <CompactMenuIcon type="guided" />
                Guided Highlights
              </button>
              <button className="menu-item" onClick={() => setCompactPanelView('skills-annotations')}>
                <CompactMenuIcon type="annotations" />
                Skills Annotations
              </button>
              <button className="menu-item" onClick={() => setCompactPanelView('suggestions')}>
                <CompactMenuIcon type="suggestions" />
                Suggestions
              </button>
            </>
          )}

          {compactPanelView === 'skills-annotations' && (
            <>
              <button
                className={`menu-item${skillsAnnotationsEnabled ? ' active' : ''}`}
                onClick={() => runCompactAction(() => onSkillsAnnotationsChange && onSkillsAnnotationsChange(true))}
              >
                <CompactMenuIcon type="guided-on" />
                Turn on
              </button>
              <button
                className={`menu-item${!skillsAnnotationsEnabled ? ' active' : ''}`}
                onClick={() => runCompactAction(() => onSkillsAnnotationsChange && onSkillsAnnotationsChange(false))}
              >
                <CompactMenuIcon type="guided-off" />
                Turn off
              </button>
            </>
          )}

          {compactPanelView === 'suggestions' && (
            <>
              <button
                className={`menu-item${suggestionsEnabled ? ' active' : ''}`}
                onClick={() => runCompactAction(() => onSuggestionsChange && onSuggestionsChange(true))}
              >
                <CompactMenuIcon type="guided-on" />
                Turn on
              </button>
              <button
                className={`menu-item${!suggestionsEnabled ? ' active' : ''}`}
                onClick={() => runCompactAction(() => onSuggestionsChange && onSuggestionsChange(false))}
              >
                <CompactMenuIcon type="guided-off" />
                Turn off
              </button>
            </>
          )}

          {compactPanelView === 'skills' && (
            <>
              <button className="menu-item" onClick={() => runCompactAction(onShowSimpleReflections)}>
                <CompactMenuIcon type="reflection" />
                {t('report_menu_simple_reflections')}
              </button>
              <button className="menu-item" onClick={() => runCompactAction(onShowAffirmations)}>
                <CompactMenuIcon type="affirm" />
                {t('report_menu_affirmations')}
              </button>
              <button className="menu-item" onClick={() => runCompactAction(onShowComplexReflections)}>
                <CompactMenuIcon type="reflection" />
                {t('report_menu_complex_reflections')}
              </button>
            </>
          )}

          {compactPanelView === 'counsellors' && Object.entries(COUNSELLOR_OVERLAY_CONFIG).map(([overlayId, config]) => (
            <button
              key={overlayId}
              className="menu-item"
              onClick={() => runCompactAction(() => onShowCounsellors && onShowCounsellors(overlayId))}
            >
              <CompactMenuIcon type="counsellors" />
              {t(config.titleKey)}
            </button>
          ))}
        </div>
      </div>
    </div>
  ) : null;

  const compactDropdownContent = menuOpen ? (
    <div
      ref={menuPortalRef}
      className={`dropdown-menu-portal compact-account-menu-portal${theme === 'dark' ? ' dark' : ''}`}
      style={{
        top: `${menuPosition.top}px`,
        right: `${menuPosition.right}px`,
        transform: menuPosition.placement === 'above' ? 'translateY(-100%)' : undefined,
      }}
    >
      <div ref={floatingMenuRef} className="dropdown-menu dropdown-menu--floating compact-account-menu">
        <div className="menu-section">
          <div className="menu-label">{t('report_menu_theme_label')}</div>
          <button
            className={`menu-item ${theme === 'light' ? 'active' : ''}`}
            onClick={() => handleThemeChange('light')}
          >
            <CompactMenuIcon type="light" />
            {t('report_theme_light')}
          </button>
          <button
            className={`menu-item ${theme === 'dark' ? 'active' : ''}`}
            onClick={() => handleThemeChange('dark')}
          >
            <CompactMenuIcon type="dark" />
            {t('report_theme_dark')}
          </button>
        </div>
        <div className="menu-section">
          <div className="menu-label">{t('report_menu_more_label')}</div>
          <button className="menu-item" onClick={() => { onShowAccount && onShowAccount(); closeMenu && closeMenu(); }}>
            <CompactMenuIcon type="account" />
            {t('report_menu_account')}
          </button>
          <button
            className="menu-item"
            onClick={() => {
              setCompactPanelOpen(true);
              closeMenu && closeMenu();
            }}
          >
            <CompactMenuIcon type="more" />
            App Menu
          </button>
          {authenticated ? (
            <button className="menu-item" onClick={() => { onSignOut && onSignOut(); closeMenu && closeMenu(); }}>
              <CompactMenuIcon type="signout" />
              {t('report_menu_signout')}
            </button>
          ) : (
            <button className="menu-item" onClick={() => { onSignIn && onSignIn(); closeMenu && closeMenu(); }}>
              <CompactMenuIcon type="signin" />
              {t('report_menu_signin')}
            </button>
          )}
        </div>
      </div>
    </div>
  ) : null;

  const dropdownContent = compactAccountMenu ? compactDropdownContent : menuOpen ? (
    <div
      ref={menuPortalRef}
      className={`dropdown-menu-portal${theme === 'dark' ? ' dark' : ''}${submenuDirection === 'right' ? ' dropdown-menu-portal--submenus-right' : ''}`}
      style={{
        top: `${menuPosition.top}px`,
        right: `${menuPosition.right}px`,
        transform: menuPosition.placement === 'above' ? 'translateY(-100%)' : undefined,
      }}
    >
      <div ref={floatingMenuRef} className="dropdown-menu dropdown-menu--floating">
        <div className="menu-section">
          <div className="menu-label">{t('report_menu_theme_label')}</div>
          <button
            className={`menu-item ${theme === 'light' ? 'active' : ''}`}
            onClick={() => handleThemeChange('light')}
          >
            <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
            {t('report_theme_light')}
          </button>
          <button
            className={`menu-item ${theme === 'dark' ? 'active' : ''}`}
            onClick={() => handleThemeChange('dark')}
          >
            <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
            {t('report_theme_dark')}
          </button>
        </div>
        <div className="menu-section">
          <div className="menu-label">{t('report_menu_more_label')}</div>
          {!hideAboutMenu && (
            <div className="menu-item-with-submenu">
              <button
                ref={aboutBtnRef}
                className={`menu-item${aboutMenuOpen ? ' active' : ''}`}
                onClick={() => {
                  setLangMenuOpen(false);
                  setSkillsMenuOpen(false);
                  setGuidedHighlightsMenuOpen(false);
                  setSettingsMenuOpen(false);
                  setAboutMenuOpen((open) => !open);
                }}
              >
                <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                {t('report_menu_about')}
              </button>
              {aboutMenuOpen && (
                <div className="language-submenu report-submenu-wide">
                <button
                  className="menu-item"
                  onClick={() => { onShowAbout && onShowAbout(); setAboutMenuOpen(false); setSkillsMenuOpen(false); closeMenu && closeMenu(); }}
                >
                  <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  {t('report_menu_about_aimie')}
                </button>
                <button
                  className="menu-item"
                  onClick={() => { onShowWorkingAlliance && onShowWorkingAlliance(); setAboutMenuOpen(false); setSkillsMenuOpen(false); setCounsellorsMenuOpen(false); closeMenu && closeMenu(); }}
                >
                  <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12c1.8-4.5 5-6.8 8-6.8s6.2 2.3 8 6.8c-1.8 4.5-5 6.8-8 6.8S5.8 16.5 4 12Z"/><path d="M9.8 12a2.2 2.2 0 1 0 4.4 0 2.2 2.2 0 0 0-4.4 0Z"/></svg>
                  {t('working_alliance_label')}
                </button>
                <button
                  className="menu-item"
                  onClick={() => { onShowLectures && onShowLectures(); setAboutMenuOpen(false); setSkillsMenuOpen(false); closeMenu && closeMenu(); }}
                >
                  <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m10 9 5 3-5 3z"/></svg>
                  Lectures
                </button>
                <button
                  className="menu-item"
                  onClick={() => { onShowClient && onShowClient(); setAboutMenuOpen(false); setSkillsMenuOpen(false); closeMenu && closeMenu(); }}
                >
                  <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  {t('report_menu_clients')}
                </button>
                <div className="menu-item-with-submenu">
                  <button
                    ref={counsellorsBtnRef}
                    className={`menu-item${counsellorsMenuOpen ? ' active' : ''}`}
                    onClick={() => {
                      setSkillsMenuOpen(false);
                      setCounsellorsMenuOpen((open) => !open);
                    }}
                  >
                    <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M19.5 21v-2a7.5 7.5 0 0 0-15 0v2"/><circle cx="12" cy="7" r="4"/><path d="M10.75 12.5 12 15l1.25-2.5"/><path d="M12 15v4"/><path d="M10.9 18.8 12 15l1.1 3.8"/></svg>
                    {t('report_menu_counsellors')}
                  </button>
                  {counsellorsMenuOpen && (
                    <div className="language-submenu report-submenu-wide">
                      {Object.entries(COUNSELLOR_OVERLAY_CONFIG).map(([overlayId, config]) => (
                        <button
                          key={overlayId}
                          className="menu-item"
                          onClick={() => {
                            onShowCounsellors && onShowCounsellors(overlayId);
                            setCounsellorsMenuOpen(false);
                            setAboutMenuOpen(false);
                            setSkillsMenuOpen(false);
                            closeMenu && closeMenu();
                          }}
                        >
                          <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M19.5 21v-2a7.5 7.5 0 0 0-15 0v2"/><circle cx="12" cy="7" r="4"/></svg>
                          {t(config.titleKey)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  className="menu-item"
                  onClick={() => { onShowTutorial && onShowTutorial(); setAboutMenuOpen(false); setSkillsMenuOpen(false); closeMenu && closeMenu(); }}
                >
                  <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z"/><path d="M9 7h6"/><path d="M9 11h8"/><path d="M9 15h5"/></svg>
                  {t('report_menu_tutorial')}
                </button>
                <button
                  className="menu-item"
                  onClick={() => { onShowAutomatedMiscMiti && onShowAutomatedMiscMiti(); setAboutMenuOpen(false); setSkillsMenuOpen(false); closeMenu && closeMenu(); }}
                >
                  <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15.5A2.5 2.5 0 0 0 17.5 16H4z"/><path d="M6.5 3v13"/><path d="M8.5 7h8"/><path d="M8.5 10.5h8"/><path d="M8.5 14h5"/></svg>
                  {t('report_menu_automated_misc_miti')}
                </button>
                <div className="menu-item-with-submenu">
                  <button
                    ref={skillsBtnRef}
                    className={`menu-item${skillsMenuOpen ? ' active' : ''}`}
                    onClick={() => {
                      setCounsellorsMenuOpen(false);
                      setSkillsMenuOpen((open) => !open);
                    }}
                  >
                    <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="10" cy="8.5" rx="4.3" ry="5.8"/><path d="M7.4 5.3 12.6 11.7"/><path d="M12.6 5.3 7.4 11.7"/><path d="M10 2.8v11.4"/><path d="M5.9 8.5h8.2"/><path d="M12.9 13.1 19.5 19.7"/><path d="M18.2 21 20.8 18.4"/><circle cx="4.4" cy="18.6" r="1.8" fill="currentColor" stroke="none"/></svg>
                    {t('report_menu_specific_skills')}
                  </button>
                  {skillsMenuOpen && (
                    <div className="language-submenu report-submenu-wide">
                      <button
                        className="menu-item"
                        onClick={() => { onShowSimpleReflections && onShowSimpleReflections(); setSkillsMenuOpen(false); setAboutMenuOpen(false); closeMenu && closeMenu(); }}
                      >
                        <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M5 8a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v4a3 3 0 0 1-3 3H10l-4 4v-4H8a3 3 0 0 1-3-3z"/><path d="M9 10h6"/></svg>
                        {t('report_menu_simple_reflections')}
                      </button>
                      <button
                        className="menu-item"
                        onClick={() => { onShowAffirmations && onShowAffirmations(); setSkillsMenuOpen(false); setAboutMenuOpen(false); closeMenu && closeMenu(); }}
                      >
                        <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 4.5 14.2 9l4.8.7-3.5 3.4.8 4.8L12 15.6 7.7 17.9l.8-4.8L5 9.7 9.8 9z"/><path d="M7 20h10"/></svg>
                        {t('report_menu_affirmations')}
                      </button>
                      <button
                        className="menu-item"
                        onClick={() => { onShowComplexReflections && onShowComplexReflections(); setSkillsMenuOpen(false); setAboutMenuOpen(false); closeMenu && closeMenu(); }}
                      >
                        <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 8a3 3 0 0 1 3-3h7a3 3 0 0 1 3 3v4a3 3 0 0 1-3 3H9l-4 4v-4H7a3 3 0 0 1-3-3z"/><path d="M14 7.5h3a3 3 0 0 1 3 3v3a3 3 0 0 1-3 3h-1.2L13 19v-3h1a3 3 0 0 0 3-3v-2.5"/><path d="M8 10h5"/></svg>
                        {t('report_menu_complex_reflections')}
                      </button>
                    </div>
                  )}
                </div>
                </div>
              )}
            </div>
          )}
          <div className="menu-item-with-submenu">
            <button
              className={`menu-item${settingsMenuOpen ? ' active' : ''}`}
              onClick={() => {
                setAboutMenuOpen(false);
                setSkillsMenuOpen(false);
                setLangMenuOpen(false);
                setGuidedHighlightsMenuOpen(false);
                setSkillsAnnotationsMenuOpen(false);
                setSuggestionsMenuOpen(false);
                setSettingsMenuOpen((open) => !open);
              }}
            >
              <CompactMenuIcon type="more" />
              Settings
            </button>
            {settingsMenuOpen && (
              <div className="language-submenu">
                <div className="menu-item-with-submenu">
                  <button
                    ref={guidedHighlightsBtnRef}
                    className={`menu-item${guidedHighlightsMenuOpen ? ' active' : ''}`}
                    onClick={() => {
                      setSkillsAnnotationsMenuOpen(false);
                      setSuggestionsMenuOpen(false);
                      setGuidedHighlightsMenuOpen((open) => !open);
                    }}
                  >
                    <CompactMenuIcon type="guided" />
                    Guided Highlights
                  </button>
                  {guidedHighlightsMenuOpen && (
                    <div className="language-submenu">
                      <button
                        className={`menu-item${guidedHighlightsEnabled ? ' active' : ''}`}
                        onClick={() => {
                          onGuidedHighlightsChange && onGuidedHighlightsChange(true);
                          setGuidedHighlightsMenuOpen(false);
                          setSettingsMenuOpen(false);
                          closeMenu && closeMenu();
                        }}
                      >
                        <CompactMenuIcon type="guided-on" />
                        Turn on
                      </button>
                      <button
                        className={`menu-item${!guidedHighlightsEnabled ? ' active' : ''}`}
                        onClick={() => {
                          onGuidedHighlightsChange && onGuidedHighlightsChange(false);
                          setGuidedHighlightsMenuOpen(false);
                          setSettingsMenuOpen(false);
                          closeMenu && closeMenu();
                        }}
                      >
                        <CompactMenuIcon type="guided-off" />
                        Turn off
                      </button>
                    </div>
                  )}
                </div>
                <div className="menu-item-with-submenu">
                  <button
                    className={`menu-item${skillsAnnotationsMenuOpen ? ' active' : ''}`}
                    onClick={() => {
                      setGuidedHighlightsMenuOpen(false);
                      setSuggestionsMenuOpen(false);
                      setSkillsAnnotationsMenuOpen((open) => !open);
                    }}
                  >
                    <CompactMenuIcon type="annotations" />
                    Skills Annotations
                  </button>
                  {skillsAnnotationsMenuOpen && (
                    <div className="language-submenu">
                      <button
                        className={`menu-item${skillsAnnotationsEnabled ? ' active' : ''}`}
                        onClick={() => {
                          onSkillsAnnotationsChange && onSkillsAnnotationsChange(true);
                          setSkillsAnnotationsMenuOpen(false);
                          setSettingsMenuOpen(false);
                          closeMenu && closeMenu();
                        }}
                      >
                        <CompactMenuIcon type="guided-on" />
                        Turn on
                      </button>
                      <button
                        className={`menu-item${!skillsAnnotationsEnabled ? ' active' : ''}`}
                        onClick={() => {
                          onSkillsAnnotationsChange && onSkillsAnnotationsChange(false);
                          setSkillsAnnotationsMenuOpen(false);
                          setSettingsMenuOpen(false);
                          closeMenu && closeMenu();
                        }}
                      >
                        <CompactMenuIcon type="guided-off" />
                        Turn off
                      </button>
                    </div>
                  )}
                </div>
                <div className="menu-item-with-submenu">
                  <button
                    className={`menu-item${suggestionsMenuOpen ? ' active' : ''}`}
                    onClick={() => {
                      setGuidedHighlightsMenuOpen(false);
                      setSkillsAnnotationsMenuOpen(false);
                      setSuggestionsMenuOpen((open) => !open);
                    }}
                  >
                    <CompactMenuIcon type="suggestions" />
                    Suggestions
                  </button>
                  {suggestionsMenuOpen && (
                    <div className="language-submenu">
                      <button
                        className={`menu-item${suggestionsEnabled ? ' active' : ''}`}
                        onClick={() => {
                          onSuggestionsChange && onSuggestionsChange(true);
                          setSuggestionsMenuOpen(false);
                          setSettingsMenuOpen(false);
                          closeMenu && closeMenu();
                        }}
                      >
                        <CompactMenuIcon type="guided-on" />
                        Turn on
                      </button>
                      <button
                        className={`menu-item${!suggestionsEnabled ? ' active' : ''}`}
                        onClick={() => {
                          onSuggestionsChange && onSuggestionsChange(false);
                          setSuggestionsMenuOpen(false);
                          setSettingsMenuOpen(false);
                          closeMenu && closeMenu();
                        }}
                      >
                        <CompactMenuIcon type="guided-off" />
                        Turn off
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {!hideContactsMenuItem && (
            <button
              className="menu-item"
              onClick={() => { onShowContacts && onShowContacts(); closeMenu && closeMenu(); }}
            >
              <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              {t('report_menu_contacts')}
            </button>
          )}
          <button
            className="menu-item"
            onClick={() => { onShowAccount && onShowAccount(); closeMenu && closeMenu(); }}
          >
            <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></svg>
            {t('report_menu_account')}
          </button>
          <div className="menu-item-with-submenu">
            <button
              ref={langBtnRef}
              className={`menu-item${langMenuOpen ? ' active' : ''}`}
              onClick={() => {
                setAboutMenuOpen(false);
                setSkillsMenuOpen(false);
                setGuidedHighlightsMenuOpen(false);
                setSettingsMenuOpen(false);
                setLangMenuOpen((open) => !open);
              }}
            >
              <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
              {t('report_menu_language')}
            </button>
            {langMenuOpen && (
              <div className="language-submenu">
                {LANGUAGE_OPTIONS.map((option) => (
                  <button
                    key={option.code}
                    className={`menu-item language-menu-item${language === option.code ? ' active' : ''}`}
                    onClick={() => { onLanguageChange && onLanguageChange(option.code); setLangMenuOpen(false); closeMenu && closeMenu(); }}
                  >
                    <span className="language-badge">{option.badge}</span>
                    <span className="language-name">{t(option.labelKey)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {authenticated ? (
            <button
              className="menu-item"
              onClick={() => { onSignOut && onSignOut(); closeMenu && closeMenu(); }}
            >
              <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              {t('report_menu_signout')}
            </button>
          ) : (
            <button
              className="menu-item"
              onClick={() => { onSignIn && onSignIn(); closeMenu && closeMenu(); }}
            >
              <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
              {t('report_menu_signin')}
            </button>
          )}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="menu-container" ref={menuRef}>
      {triggerVariant === 'custom' ? (
        <button
          type="button"
          className={triggerClassName}
          onClick={toggleMenu}
          ref={triggerRef}
          aria-label={triggerAriaLabel}
          aria-expanded={menuOpen}
        >
          {triggerContent}
        </button>
      ) : (
        <button className="hamburger-menu" onClick={toggleMenu} ref={triggerRef}>
          <span></span>
          <span></span>
          <span></span>
        </button>
      )}
      {typeof document !== 'undefined' ? createPortal(dropdownContent, document.body) : null}
      {typeof document !== 'undefined' && compactAccountMenu ? createPortal(compactPanelContent, document.body) : null}
    </div>
  );
}

export default ReportHamburgerMenu;
